import _styled from "styled-components";
const _excluded = ["type", "style", "className", "disabled", "error", "renderIcon", "renderLeftIcon", "renderLeftElement", "renderRightElement", "onKeyDown", "onChange", "softLimit", "maxLength", "showCountThreshold", "value", "defaultValue", "showCount", "inputClassName"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styledC from 'styled-components';
import theme, { getInteractiveStyles, stylePropType } from '@adsk/alloy-react-theme';
import { ICON_SIZE, LEFT_PADDING, RIGHT_PADDING, TEXT_INPUT_TYPES } from './consts';
import { useDeprecatedRenderProps, useTextInput } from './hooks';
const getElementStyle = (disabled, error) => _objectSpread(_objectSpread({
  color: theme.tokens.colors.text.secondary.value,
  pointerEvents: 'none',
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)'
}, disabled && {
  color: theme.tokens.colors.text.inactive.value
}), error && {
  color: theme.tokens.colors.text.error.value
});
const StyledDiv = styledC.div.withConfig({
  displayName: "TextInput__StyledDiv",
  componentId: "sc-830v95-0"
})(["height:36px;position:relative;width:100%;background:", ";color:", ";border:1px solid ", ";border-radius:", "px;box-sizing:border-box;padding-left:", "px;padding-right:", "px;", ";&:disabled{background:", ";border-color:", ";box-shadow:none;color:", ";cursor:not-allowed;}border-color:", ";&:focus:not(:active){border-color:", ";}"], theme.tokens.colors.background.primary.value, theme.tokens.colors.text.primary.value, theme.tokens.colors.border.value, theme.borderRadius.small, LEFT_PADDING, RIGHT_PADDING, props => getInteractiveStyles(props.disabled), theme.tokens.colors.background.neutral.inactive.value, theme.tokens.colors.border.inactive.value, theme.tokens.colors.text.inactive.value, props => props.$error ? theme.tokens.colors.border.error.value : undefined, props => props.$error ? undefined : theme.tokens.colors.border.focused.value);
var _StyledStyledDiv = styledC(StyledDiv).withConfig({
  displayName: "TextInput___StyledStyledDiv",
  componentId: "sc-830v95-1"
})(["", ""], p => p.$_css);
const StyledInput = styledC.input.withConfig({
  displayName: "TextInput__StyledInput",
  componentId: "sc-830v95-2"
})(["", ";height:100%;left:0px;outline:none;padding:0px ", "px 1px;position:absolute;width:100%;border:none;color:", ";", ";&::placeholder{color:", ";}&:disabled{color:", ";background-color:", ";}"], theme.typography.labelMedium, theme.spacing.S, theme.tokens.colors.text.primary.value, props => getInteractiveStyles(!!props.disabled), theme.tokens.colors.text.tertiary.value, theme.tokens.colors.text.inactive.value, theme.tokens.colors.background.secondary.value);

/**
 * A TextInput component that optionally displays an icon.
 * All arbitrary DOM properties and attributes (including event handlers) are accepted and passed to the HTML input element.
 */

const TextInput = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      type = TEXT_INPUT_TYPES.TEXT,
      style,
      className,
      disabled = false,
      error = false,
      renderIcon,
      renderLeftIcon,
      renderLeftElement,
      renderRightElement,
      onKeyDown,
      onChange,
      softLimit,
      maxLength,
      showCountThreshold = 15,
      value,
      defaultValue,
      showCount,
      inputClassName
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const {
    renderLeft,
    renderRight
  } = useDeprecatedRenderProps({
    renderIcon,
    renderLeftIcon,
    renderLeftElement,
    renderRightElement
  });
  const {
    refs: {
      divRef,
      leftRef,
      rightRef
    },
    padding: {
      paddingLeft,
      paddingRight
    },
    propGetters: {
      getInputProps
    },
    charCount,
    shouldRenderCount
  } = useTextInput({
    renderLeft,
    renderRight,
    onKeyDown,
    onChange,
    type,
    ref,
    showCount,
    softLimit,
    maxLength,
    showCountThreshold,
    value,
    defaultValue
  });
  const renderCount = useCallback(props => {
    if (shouldRenderCount) {
      return /*#__PURE__*/React.createElement("div", _extends({}, props, {
        "data-testid": "TextInput__count"
      }), `${charCount}/${softLimit || maxLength}`);
    }
    return null;
  }, [softLimit, charCount, maxLength, shouldRenderCount]);
  const renderRightComponent = useMemo(() => {
    return renderRight !== null && renderRight !== void 0 ? renderRight : renderCount;
  }, [renderRight, renderCount]);
  return /*#__PURE__*/React.createElement(_StyledStyledDiv, {
    ref: divRef,
    className: className,
    disabled: disabled,
    $error: error,
    $_css: style || {}
  }, renderLeft === null || renderLeft === void 0 ? void 0 : renderLeft({
    size: ICON_SIZE,
    ref: leftRef,
    style: _objectSpread(_objectSpread({}, getElementStyle(disabled, error)), {}, {
      left: paddingLeft,
      zIndex: 1
    })
  }), /*#__PURE__*/React.createElement(StyledInput, _extends({}, props, getInputProps(), {
    disabled: disabled,
    maxLength: maxLength,
    className: inputClassName
  })), renderRightComponent === null || renderRightComponent === void 0 ? void 0 : renderRightComponent({
    size: ICON_SIZE,
    ref: rightRef,
    style: _objectSpread(_objectSpread({}, getElementStyle(disabled, error)), {}, {
      right: paddingRight
    })
  }));
});
TextInput.displayName = 'TextInput';
TextInput.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Toggle disabled state */
  disabled: PropTypes.bool,
  /** Toggle error state */
  error: PropTypes.bool,
  /** Deprecated: Display icon adjacent to input text on right side */
  renderIcon: PropTypes.func,
  /** Deprecated: Display icon adjacent to input text on left side */
  renderLeftIcon: PropTypes.func,
  /** Placeholder text value */
  placeholder: PropTypes.string,
  /** Type of input value */
  type: PropTypes.oneOf(Object.values(TEXT_INPUT_TYPES)),
  /** Display a React element adjacent to input text on left side */
  renderLeftElement: PropTypes.func,
  /** Display a React element adjacent to input text on right side */
  renderRightElement: PropTypes.func,
  /** Like maxLength but this is a soft threshold and the input does not stop the user from entering characters beyond this amount, but this will show as the maximum number in the count display */
  softLimit: PropTypes.number,
  /** A hard limit on the maximum number of characters permitted in the input field, if softLimit is not provided then this will show as the maximum number in the count display */
  maxLength: PropTypes.number,
  /** If set, this will delay the display of character count until the character count is within this many characters of reaching the maximum length or soft limit length. Default is 15. Set to 0 to always show the count when showCount is enabled. */
  showCountThreshold: PropTypes.number,
  /** If true and maxLength or softLimit is set then display a character count, otherwise do not display the character count */
  showCount: PropTypes.bool,
  /** Class applied to the input element */
  inputClassName: PropTypes.string
};
export default Object.assign(TextInput, {
  TYPES: TEXT_INPUT_TYPES
});
//# sourceMappingURL=TextInput.js.map